/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * ContextSidebar (#683, W2 Phase 1) — the surfaces of the *active* workflow
 * group only, rather than the whole application tree.
 *
 * Renders nothing for a `direct` tab (Dashboard, Session Simulator, or a group
 * a role can only see one surface of): those take the full width. Collapses to
 * a 56px icon rail, persisted per browser.
 *
 * Per #683 D5 the old resizable width is gone — a one-group list has a natural
 * size, so `adminConsole.sidebarWidth` / `sidebarCollapsed` are retired in
 * favour of the single `railCollapsed` flag (see `rail-storage.ts`).
 */

import React from 'react';
import { Box, chakra, Flex, Text } from '@chakra-ui/react';
import { NavLink } from 'react-router-dom';
import { useGuardedLinkProps } from '../ui/UnsavedWorkGuard';
import type { NavItem } from '../../utils/rbac';
import { NavIcon } from './nav-icons';

const RAIL_WIDTH = '56px';
const EXPANDED_WIDTH = '224px';

export interface ContextSidebarProps {
  /** Label of the active workflow group (the sidebar's heading). */
  label: string;
  items: NavItem[];
  collapsed: boolean;
  onToggle: () => void;
}

/**
 * One sidebar item, guarded (`#1245`).
 *
 * A component rather than an inline `NavLink`, because {@link useGuardedLinkProps} is a hook and the
 * items are a `.map` — and because a guard written inline in a list is the copy that gets forgotten
 * when a second list appears.
 */
const SidebarLink: React.FC<{
  item: NavItem;
  collapsed: boolean;
  children: (state: { isActive: boolean }) => React.ReactNode;
}> = ({ item, collapsed, children }) => (
  <NavLink
    to={item.path}
    title={collapsed ? item.label : undefined}
    {...useGuardedLinkProps(item.path)}
    style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
  >
    {children}
  </NavLink>
);

export const ContextSidebar: React.FC<ContextSidebarProps> = ({ label, items, collapsed, onToggle }) => (
  <Box
    as="nav"
    aria-label={`${label} surfaces`}
    w={collapsed ? RAIL_WIDTH : EXPANDED_WIDTH}
    flexShrink={0}
    bg="bg.card"
    borderRightWidth="1px"
    borderRightColor="border.default"
    display="flex"
    flexDirection="column"
    pt={2}
    transition="width 0.2s"
    overflowX="hidden"
  >
    {collapsed ? (
      <Box h="14px" />
    ) : (
      <Text
        px={4}
        pt={2}
        pb={2}
        fontSize="10.5px"
        fontWeight="600"
        letterSpacing="0.08em"
        textTransform="uppercase"
        color="fg.muted"
        whiteSpace="nowrap"
      >
        {label}
      </Text>
    )}

    <Box flex={1} overflowY="auto">
      {items.map((item) => (
        /* Guarded like every other way out of a surface (`#1245`); see `DrawerLink` for why it stays
           an anchor. Extracted to a component because the guard is a hook and this is a `.map`. */
        <SidebarLink key={item.path} item={item} collapsed={collapsed}>
          {({ isActive }) => (
            <Flex
              align="center"
              justify={collapsed ? 'center' : 'flex-start'}
              gap={2.5}
              px={collapsed ? 0 : 4}
              py={collapsed ? 3 : 2.5}
              fontSize="13.5px"
              color={isActive ? 'accent.solid' : 'fg.default'}
              fontWeight={isActive ? '600' : '400'}
              bg={isActive ? 'accent.subtle' : 'transparent'}
              borderLeftWidth="3px"
              borderLeftColor={isActive ? 'accent.solid' : 'transparent'}
              _hover={{ bg: isActive ? 'accent.subtle' : 'bg.subtle' }}
              transition="background 0.15s"
            >
              <NavIcon name={item.icon} size={16} />
              {!collapsed && <Text whiteSpace="nowrap">{item.label}</Text>}
            </Flex>
          )}
        </SidebarLink>
      ))}
    </Box>

    <chakra.button
      type="button"
      aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      title={collapsed ? 'Expand' : 'Collapse'}
      onClick={onToggle}
      display="flex"
      alignItems="center"
      justifyContent={collapsed ? 'center' : 'flex-start'}
      gap={2}
      px={collapsed ? 0 : 4}
      py={2.5}
      w="100%"
      fontSize="xs"
      color="fg.muted"
      borderTopWidth="1px"
      borderTopColor="border.default"
      _hover={{ color: 'fg.default', bg: 'bg.subtle' }}
    >
      <NavIcon name={collapsed ? 'chevrons-right' : 'chevrons-left'} size={16} />
      {!collapsed && <Text whiteSpace="nowrap">Collapse</Text>}
    </chakra.button>
  </Box>
);
